import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import MainContainer from "../../components/MainContainer";
import useFeedView from "../../hooks/useFeedView";
import { prefetchVideos } from "../../utils/videoPrefetch";
import {
  FeedEndMarker,
  FeedList,
  FeedLoader,
  FeedSentinel,
  FeedSkeleton,
  FeedSkeletonFrame,
  FeedSkeletonList,
  WaterSeparator,
} from "../HomePage/HomePage.styles";
import LineWater from "../../assets/images/waterSeparator.webp";
import CollectionFilter from "../../components/CollectionFilter/CollectionFilter";
import TabsComponent from "../../components/utils/TabsComponent/TabsComponent";
import { useDispatch, useSelector } from "react-redux";
import {
  createFolder,
  deleteCollection,
  deleteFolder,
  editFolder,
  generateCollection,
  getCollections,
  getFolders,
  putCollectionToFolder,
  rateModel,
  toggleToFavorite,
} from "../../store/slices/model/asyncThunks";
import { useNavigate, useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  selectCollectionFolders,
  selectCollections,
  selectCreateFolderPending,
  selectDeleteCollectionPending,
  selectDeleteFolderPending,
  selectEditFolderPending,
  selectPutCollectionFolderPending,
} from "../../store/slices/model/slice";
import Pagination from "../../components/Pagination/Pagination";
import {
  BigRow,
  BigRowMobile,
  Button,
  ButtonText,
  CollectionButton,
  Toolbar,
  Column,
  Container,
  Content,
  ContentContainer,
  DeleteButton,
  DownloadButton,
  EmptyState,
  PaginationWrapper,
  Row,
} from "./CollectionPage.styles";
import CollectionModal from "../../components/CollectionModal/CollectionModal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { selectUser } from "../../store/slices/user/slice";
import CollectionItem from "../../components/utils/CollectionItem/CollectionItem";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import { toast } from "sonner";
import {
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_RESOLUTION,
  COLLECTION_STATUS,
  MEDIA_TYPE,
  VIDEO_DURATION_PRICE,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";
import CreateFolderModal from "../../components/CreateFolderModal/CreateFolderModal";
import AddToFolderModal from "../../components/AddToFolderModal/AddToFolderModal";
import EditOrDeleteFolderModal from "../../components/EditOrDeleteFolderModal/EditOrDeleteFolderModal";
import CollectionSettingsModal from "../../components/CollectionSettingsModal/CollectionSettingsModal";
import { useMediaQuery } from "react-responsive";
import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import EnterPromptModal from "../../components/EnterPromptModal/EnterPromptModal";
import ExtendVideoModal from "../../components/ExtendVideoModal/ExtendVideoModal";
import axios from "axios";
import { isImageOutput } from "../../utils/utils";

import deleteAnimation from "../../assets/animations/trash.json";
import downloadAnimation from "../../assets/animations/download.json";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import AboutFapifyAccordion from "../../components/AboutFapifyAccordion/AboutFapifyAccordion";
import SeoHead from "../../components/Seo/SeoHead.js";
import { buildPageTitle } from "../../seo/siteSeo.js";

/* ExploreFeedItem only renders in feed view; lazy-load it so the heavier
   feed-only logic doesn't sit in the main bundle for grid-view users. */
const ExploreFeedItem = lazy(
  () => import("../../components/ExploreFeedItem/ExploreFeedItem"),
);

const COLLECTION_POLL_INTERVAL = 30000;
const COLLECTION_FETCH_RETRY_COUNT = 2;
const COLLECTION_FETCH_RETRY_DELAY_MS = 2000;
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const hasCompletedCollectionInDirection = (items, startIndex, direction) => {
  for (
    let index = startIndex;
    index >= 0 && index < items?.length;
    index += direction
  ) {
    if (items[index]?.status === COLLECTION_STATUS.COMPLETED) {
      return true;
    }
  }

  return false;
};

const CollectionPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const filter = searchParams.get("filter") || "ALL";

  const [searchValue, setSearchValue] = useState(() => search ?? "");
  const loggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  const deleteCollectionPending = useSelector(selectDeleteCollectionPending);
  const {
    data,
    loading,
    total,
    maxReached,
    page: activePage,
    hasDataBeenFetched,
  } = useSelector(selectCollections);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [openCollectionModal, setOpenCollectionModal] = useState(false);
  const [selectedCollectionVideo, setSelectedCollectionVideo] = useState(null);
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isAddToFolderModalOpen, setIsAddToFolderModalOpen] = useState(false);
  const [isEditOrDeleteFolderModalOpen, setIsEditOrDeleteFolderModalOpen] =
    useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");

  const [settingsPrompt, setSettingsPrompt] = useState("");

  const [showPrompts, setShowPrompts] = useState(false);
  const [isEnterPromptModalOpen, setIsEnterPromptModalOpen] = useState(false);
  const [showExtendVideoModal, setShowExtendVideoModal] = useState(false);
  const [selectedDeleteFolder, setSelectedDeleteFolder] = useState(null);

  const [select, setSelect] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);

  const { data: folders } = useSelector(selectCollectionFolders);

  const createFolderLoading = useSelector(selectCreateFolderPending);
  const addCollectionToFolderLoading = useSelector(
    selectPutCollectionFolderPending,
  );
  const deleteFolderLoading = useSelector(selectDeleteFolderPending);
  const editFolderLoading = useSelector(selectEditFolderPending);

  const [customSeed, setCustomSeed] = useState(
    Math.floor(Math.random() * 10000000) + 1,
  );
  const [videoResolution, setVideoResolution] = useState(
    localStorage.getItem("videoResolution") ?? DEFAULT_VIDEO_RESOLUTION,
  );

  const [videoDuration, setVideoDuration] = useState(
    Number(localStorage.getItem("videoDuration") ?? DEFAULT_VIDEO_DURATION),
  );
  const [selectedModelForExtend, setSelectedModelForExtend] = useState(null);
  const [extendVideoPrice, setExtendVideoPrice] = useState(30);
  const [selectedExtendCollection, setSelectedExtendCollection] =
    useState(null);

  const [navLoading, setNavLoading] = useState(false);

  // Only apply the global feed-scroll-mode class when the user is actually
  // logged in — otherwise the empty "Login Required" card gets pushed into
  // the locked-viewport feed layout and ends up overlapping the header.
  const [isFeedView, setIsFeedView] = useFeedView({ applyClass: !!loggedIn });

  const fetchCollectionsWithRetry = useCallback(
    async (params) => {
      for (
        let attempt = 0;
        attempt <= COLLECTION_FETCH_RETRY_COUNT;
        attempt += 1
      ) {
        try {
          return await dispatch(getCollections(params)).unwrap();
        } catch (error) {
          if (attempt === COLLECTION_FETCH_RETRY_COUNT) throw error;
          await pause(COLLECTION_FETCH_RETRY_DELAY_MS);
        }
      }
      return null;
    },
    [dispatch],
  );

  const requestCollections = useCallback(
    (params) => {
      void fetchCollectionsWithRetry(params).catch(() => {});
    },
    [fetchCollectionsWithRetry],
  );

  /* ── Feed-view virtualization (mirrors HomePage) ───────────────────── */
  const [feedScrollEl, setFeedScrollEl] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemHeight, setItemHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 0,
  );

  const { ref: feedSentinelRef, inView: feedSentinelInView } = useInView({
    root: feedScrollEl,
    rootMargin: "800px 0px",
    threshold: 0,
  });

  useLayoutEffect(() => {
    if (!feedScrollEl) return;
    const update = () => {
      const h = feedScrollEl.clientHeight;
      if (h > 0) {
        setItemHeight((prev) => (prev === h ? prev : h));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(feedScrollEl);
    return () => ro.disconnect();
  }, [feedScrollEl]);

  useEffect(() => {
    if (!feedScrollEl) return;
    setActiveIndex(0);
  }, [feedScrollEl]);

  useEffect(() => {
    if (!feedScrollEl || !itemHeight) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const idx = Math.max(
          0,
          Math.round(feedScrollEl.scrollTop / itemHeight),
        );
        setActiveIndex((prev) => (prev === idx ? prev : idx));
      });
    };
    feedScrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      feedScrollEl.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [feedScrollEl, itemHeight]);

  useEffect(() => {
    if (!data?.length) return;
    setActiveIndex((prev) => Math.min(prev, data.length - 1));
  }, [data?.length]);

  useEffect(() => {
    if (!isFeedView || !data?.length) return;
    const urls = [];
    for (let offset = 2; offset <= 4; offset += 1) {
      const it = data[activeIndex + offset];
      if (!it) continue;
      const isVideo = it?.output?.type === MEDIA_TYPE.VIDEO;
      const url = isVideo
        ? it?.output?.video?.compressedUrl || it?.output?.video?.url
        : null;
      if (url) urls.push(url);
    }
    if (urls.length) prefetchVideos(urls);
  }, [activeIndex, data, isFeedView]);

  const handleToggleViewMode = useCallback(() => {
    setIsFeedView((prev) => !prev);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [setIsFeedView]);

  const handleNavigateCollection = useCallback(
    async (direction) => {
      if (!selectedCollectionVideo || !data?.length) return;

      const currentIndex = data.findIndex(
        (item) => item._id === selectedCollectionVideo._id,
      );
      const nextIndex = currentIndex + direction;
      let nextCollection = null;

      for (
        let index = nextIndex;
        index >= 0 && index < data?.length;
        index += direction
      ) {
        if (data[index]?.status === COLLECTION_STATUS.COMPLETED) {
          nextCollection = data[index];
          break;
        }
      }

      // Navigate within current page
      if (nextCollection) {
        setSelectedCollectionVideo(nextCollection);
        return;
      }

      const shouldNavigateToNextPage = direction > 0;
      const shouldNavigateToPreviousPage = direction < 0;

      // Navigate to next page
      if (shouldNavigateToNextPage) {
        const totalPages = Math.ceil(total / 30);
        if (page >= totalPages) return; // No more pages

        setNavLoading(true);
        try {
          const result = await fetchCollectionsWithRetry({
            page: page + 1,
            search,
            filter,
          });

          // Update URL parameters
          searchParams.set("page", page + 1);
          setSearchParams(searchParams);

          // Select first completed item of the new page
          const firstCompletedCollection = result?.data?.find(
            (item) => item?.status === COLLECTION_STATUS.COMPLETED,
          );

          if (firstCompletedCollection) {
            setSelectedCollectionVideo(firstCompletedCollection);
          }
        } catch (err) {
          console.log(err);
          toast.error("Failed to load next page");
        } finally {
          setNavLoading(false);
        }
        return;
      }

      // Navigate to previous page
      if (shouldNavigateToPreviousPage) {
        if (page <= 1) return; // No previous page

        setNavLoading(true);
        try {
          const result = await fetchCollectionsWithRetry({
            page: page - 1,
            search,
            filter,
          });

          searchParams.set("page", page - 1);
          setSearchParams(searchParams);

          // Select last completed item of the previous page
          const previousPageCollections = result?.data ?? [];
          const lastCompletedCollection = [...previousPageCollections]
            .reverse()
            .find((item) => item?.status === COLLECTION_STATUS.COMPLETED);

          if (lastCompletedCollection) {
            setSelectedCollectionVideo(lastCompletedCollection);
          }
        } catch (err) {
          console.log(err);
          toast.error("Failed to load previous page");
        } finally {
          setNavLoading(false);
        }
      }
    },
    [
      data,
      selectedCollectionVideo,
      page,
      total,
      search,
      filter,
      fetchCollectionsWithRetry,
      searchParams,
      setSearchParams,
    ],
  );

  // Check prev/next
  const hasPrev = useMemo(() => {
    if (!selectedCollectionVideo || !data?.length) return false;
    const currentIndex = data.findIndex(
      (item) => item._id === selectedCollectionVideo._id,
    );
    return (
      hasCompletedCollectionInDirection(data, currentIndex - 1, -1) || page > 1
    );
  }, [selectedCollectionVideo, data, page]);

  const hasNext = useMemo(() => {
    if (!selectedCollectionVideo || !data?.length) return false;
    const currentIndex = data.findIndex(
      (item) => item._id === selectedCollectionVideo._id,
    );
    const totalPages = Math.ceil(total / 30);
    return (
      hasCompletedCollectionInDirection(data, currentIndex + 1, 1) ||
      page < totalPages
    );
  }, [selectedCollectionVideo, data, page, total]);

  const changeHandler = useCallback(
    (text) => {
      searchParams.set("search", text);
      searchParams.set("page", 1);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler],
  );

  const handleSetFilter = (filter) => {
    searchParams.set("page", 1);
    searchParams.set("filter", filter);
    setSearchParams(searchParams);
  };

  const isLargeDesktop = useMediaQuery({ maxWidth: 1600 });
  const isDesktop = useMediaQuery({ maxWidth: 1440 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      case isLargeDesktop:
        return 5;
      default:
        return 6;
    }
  }, [isLargeDesktop, isDesktop, isTable, isMobile]);

  const setPage = (page) => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });

    searchParams.set("page", page);
    setSearchParams(searchParams);
  };

  /* Mount-time refetch keeps cached items visible while we revalidate, so
     swapping back from /explore doesn't blink the skeleton. Subsequent
     param changes (filter/search/page) still do a normal fetch.

     Gate preserveData on hasDataBeenFetched — generateCollection.fulfilled
     can prepend a single in-progress item into a never-loaded cache, and
     preserveData's "newer than first" merge would then drop every older
     server item, leaving only the in-progress card visible. */
  const isInitialFetchRef = useRef(true);
  useEffect(() => {
    if (!loggedIn) return;
    const isMount = isInitialFetchRef.current;
    isInitialFetchRef.current = false;
    requestCollections({
      page,
      search,
      filter,
      preserveData: isMount && hasDataBeenFetched,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, loggedIn, filter, requestCollections]);

  /* In feed view, when the sentinel near the bottom enters view, fetch the
     next page in `append` mode so items accumulate instead of replacing. */
  useEffect(() => {
    if (!isFeedView) return;
    if (!feedSentinelInView) return;
    if (loading) return;
    if (maxReached) return;
    if (!data?.length) return;
    if (!loggedIn) return;

    requestCollections({
      page: (activePage || 1) + 1,
      search,
      filter,
      append: true,
    });
  }, [
    feedSentinelInView,
    isFeedView,
    loading,
    maxReached,
    data?.length,
    activePage,
    search,
    filter,
    loggedIn,
    requestCollections,
  ]);

  useEffect(() => {
    if (loggedIn) {
      dispatch(getFolders());
    }
  }, [dispatch, loggedIn]);

  const hasInProgressCollections = useMemo(
    () =>
      Array.isArray(data) &&
      data.some(
        (collection) =>
          collection?.status === COLLECTION_STATUS.IN_PROGRESS ||
          collection?.status === COLLECTION_STATUS.NUDIFY_IN_PROGRESS,
      ),
    [data],
  );

  useEffect(() => {
    if (!loggedIn || !hasInProgressCollections || page !== 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      requestCollections({
        page,
        search,
        filter,
        preserveData: true,
      });
    }, COLLECTION_POLL_INTERVAL);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    filter,
    hasInProgressCollections,
    loggedIn,
    page,
    requestCollections,
    search,
  ]);

  const handleDeleteConfirm = () => {
    if (selectedCollections?.length > 0) {
      dispatch(deleteCollection(selectedCollections))
        .unwrap()
        .then(() => {
          setShowDeleteConfirm(false);
          setSelectedCollections([]);
          setSelect(false);
        })
        .catch((err) => {
          console.log(err);
          setShowDeleteConfirm(false);
          toast.error(err.message);
        });
    } else {
      if (selectedCollectionVideo) {
        dispatch(deleteCollection([selectedCollectionVideo._id]))
          .unwrap()
          .then(() => {
            setShowDeleteConfirm(false);
            setOpenCollectionModal(false);
            setSelectedCollectionVideo(null);
          })
          .catch((err) => {
            console.log(err);
            setShowDeleteConfirm(false);
            toast.error(err.message);
          });
      }
    }
  };

  const handleRateCollection = (rating) => {
    if (selectedCollectionVideo) {
      setSelectedCollectionVideo((prev) => {
        return { ...prev, rating };
      });

      dispatch(rateModel({ collectionId: selectedCollectionVideo._id, rating }))
        .unwrap()
        .then(() => {})
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const handleCollectionModalClose = () => {
    setOpenCollectionModal(false);
    setSelectedCollectionVideo(null);
  };

  if (loggedIn === null) {
    return null;
  }

  const handleCollectionItemClick = (video) => {
    setSelectedCollectionVideo(video);
    setOpenCollectionModal(true);
  };

  const openCreateFolderModal = () => {
    setIsCreateFolderModalOpen(true);
  };

  const handleCreateFolder = () => {
    if (isCreatingNew) {
      dispatch(createFolder({ name: folderName }))
        .unwrap()
        .then(() => {
          setFolderName("");
          setIsCreateFolderModalOpen(false);
          setIsAddToFolderModalOpen(true);
        })
        .catch((err) => {
          console.log(err);
          toast.error(err.message ?? "Error occurred while creating folder");
        });
    } else {
      dispatch(createFolder({ name: folderName }))
        .unwrap()
        .then(() => {
          setFolderName("");
          setIsCreateFolderModalOpen(false);
        })
        .catch((err) => {
          console.log(err);
          toast.error(err.message ?? "Error occurred while creating folder");
        });
    }
  };

  const handleAddToFolder = ({ folderIds, toggleFavorite }) => {
    const requestData = {
      collectionId: selectedCollectionVideo._id,
      folderIds,
      currentFolderId: filter,
    };
    dispatch(putCollectionToFolder(requestData))
      .unwrap()
      .then((updatedCollection) => {
        setSelectedCollectionVideo(updatedCollection);
        setIsAddToFolderModalOpen(false);
        setOpenCollectionModal(false);
        if (toggleFavorite) {
          dispatch(
            toggleToFavorite({
              collectionId: selectedCollectionVideo._id,
              currentFolderId: filter,
            }),
          );
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error occurred while adding collection to folder");
      });
  };

  const handleDeleteFolderModal = () => {
    dispatch(deleteFolder({ folderId: selectedDeleteFolder._id }))
      .unwrap()
      .then(() => {
        setIsEditOrDeleteFolderModalOpen(false);
        setSelectedDeleteFolder(null);
        if (String(selectedDeleteFolder._id) === filter) {
          handleSetFilter("ALL");
        }
      })
      .catch((err) => {
        toast.error("Error occurred while deleting folder");
      });
  };

  const handleEditFolder = ({ folderId, name }) => {
    dispatch(editFolder({ folderId, name }))
      .unwrap()
      .then(() => {
        setIsEditOrDeleteFolderModalOpen(false);
        setSelectedDeleteFolder(null);
        toast.success("Folder updated successfully!");
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error occurred while updating folder");
      });
  };

  const handleCopyPrompt = () => {
    setIsSettingsModalOpen(false);
    if (!settingsPrompt) return;
    navigator.clipboard
      .writeText(settingsPrompt)
      .then(() => {
        toast.success("Prompt copied!");
      })
      .catch(() => {
        toast.error("Failed to copy.");
      });
  };

  const selectAllCollections = () => {
    setSelectedCollections(data?.map((v) => v._id));
  };

  const handleDownloadSelected = async () => {
    if (!selectedCollections?.length || !user) return;

    const collectionsToDownload =
      data?.filter((collection) =>
        selectedCollections.includes(collection._id),
      ) || [];

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS && collectionsToDownload?.length > 1) {
      toast.info(
        `Not supported on mobile devices. On mobile and iPad, it's likely impossible to download multiple files directly due to device limitations. You can use a computer for batch downloading or download them one by one manually.`,
      );
      return;
    }

    toast.success(
      `Starting download of ${collectionsToDownload?.length} videos...`,
    );

    for (let i = 0; i < collectionsToDownload?.length; i++) {
      const collection = collectionsToDownload[i];

      try {
        let mediaUrl = null;
        if (collection.output.type === MEDIA_TYPE.IMAGE) {
          mediaUrl = collection.output.image.url;
        } else {
          mediaUrl = collection.output.video.url;
        }

        if (!mediaUrl) {
          toast.error(`No media URL for ${collection.name}`);
          continue;
        }

        window.location.href = mediaUrl;

        if (i < collectionsToDownload?.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (err) {
        console.error(`Failed to download video ${collection.name}:`, err);
        console.error("Error details:", {
          collectionOutput: collection.output,
          errorMessage: err.message,
          errorStack: err.stack,
        });
        toast.error(`Failed to download ${collection.name}: ${err.message}`);
      }
    }

    toast.success("Downloads completed!");
  };

  const onGenerateClick = async () => {
    const isImage = selectedCollectionVideo?.output?.type === MEDIA_TYPE.IMAGE;
    const url = isImage
      ? selectedCollectionVideo?.output?.image?.url
      : selectedCollectionVideo?.output?.video?.url;

    if (!url) {
      toast.error(
        isImage
          ? "Failed to reuse generated image."
          : "Failed to reuse generated video.",
      );
      return;
    }

    const response = await axios.get(url, {
      responseType: "blob",
    });

    const urlObj = new URL(url);
    const filename = urlObj.pathname.split("/").pop() || "file";

    const file = new File([response.data], filename, {
      type: response.data.type || "application/octet-stream",
    });

    const payload = {
      customPrompt,
      negativePrompt,
      modelId: selectedModelForExtend,
      resolution: videoResolution,
      seed: customSeed,
      videoDuration,
      ...(selectedCollectionVideo?.model && {
        extendModelId: selectedCollectionVideo.model,
      }),
    };

    if (isImage) {
      payload.image = { file };
    } else {
      payload.video = { file };
    }

    dispatch(generateCollection(payload))
      .unwrap()
      .then(() => {
        toast.success(
          isImage ? "Animation started..." : "Extending started...",
        );

        const alreadyGeneratedVideo = localStorage.getItem(
          "alreadyGeneratedVideo",
        );

        if (!alreadyGeneratedVideo) {
          navigate("/mine");

          window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant",
          });
        }

        setCustomPrompt("");
        setNegativePrompt("");
        setIsEnterPromptModalOpen(false);
        setShowExtendVideoModal(false);
        handleCollectionModalClose();
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  const calculateTotalPrice = (modelPrice) => {
    let totalPrice = modelPrice ?? 30;

    if (videoResolution && VIDEO_RESOLUTION_PRICE[videoResolution]) {
      totalPrice += VIDEO_RESOLUTION_PRICE[videoResolution];
    }

    if (videoDuration && VIDEO_DURATION_PRICE[videoDuration]) {
      totalPrice += VIDEO_DURATION_PRICE[videoDuration];
    }

    return totalPrice;
  };

  return (
    <MainContainer
      hideTabs={false}
      fullWidth
      hideOverflowX={isFeedView}
      isFeedView={isFeedView}
      onToggleViewMode={handleToggleViewMode}
      showViewModeToggle
    >
      <SeoHead
        title={buildPageTitle("Your Collection")}
        description="Manage your Fapify collection, generated videos, saved folders, and prompt variations."
        path="/mine"
        noindex
      />
      <Container>
        <Toolbar $feed={isFeedView}>
          {/* Always rendered — visible in both grid and feed layouts so the
              view-mode toggle is reachable from either. */}
          <TabsComponent
            collectionPage
            isFeedView={isFeedView}
            onToggleViewMode={handleToggleViewMode}
            showViewModeToggle
          />
          {!isFeedView && (
            <CollectionFilter
              hideFilter
              showSelect
              showFolders
              folders={folders ?? []}
              setFilter={handleSetFilter}
              filter={filter}
              searchValue={searchValue}
              onSearchChange={(value) => {
                setSearchValue(value);
                debouncedChangeHandler(value);
              }}
              openCreateFolderModal={openCreateFolderModal}
              openDeleteFolder={() => setIsEditOrDeleteFolderModalOpen(true)}
              setSelectedDeleteFolder={setSelectedDeleteFolder}
              setSelect={setSelect}
              select={select}
              collectionPage
              selectedCollections={selectedCollections}
            />
          )}
        </Toolbar>

        {select && (
          <BigRow>
            <Row>
              <CollectionButton onClick={selectAllCollections}>
                <ButtonText $click>Select All</ButtonText>
              </CollectionButton>
              <ButtonText
                $click
                onClick={() => {
                  setSelectedCollections([]);
                }}
              >
                Clear
              </ButtonText>
            </Row>
            <Row>
              <DeleteButton
                disabled={!selectedCollections?.length || !user}
                onClick={() => {
                  if (!selectedCollections?.length || !user) return;
                  setShowDeleteConfirm(true);
                }}
              >
                <LottieIcon
                  animationData={deleteAnimation}
                  trigger="hover"
                  size={24}
                />
              </DeleteButton>
              <DownloadButton
                $white
                onClick={handleDownloadSelected}
                style={{
                  pointerEvents:
                    !selectedCollections?.length || !user ? "none" : "all",
                }}
              >
                <LottieIcon
                  animationData={downloadAnimation}
                  trigger="click"
                  size={24}
                />
              </DownloadButton>
            </Row>
          </BigRow>
        )}
        {select && (
          <BigRowMobile>
            <Row>
              {/* {!select ? (
                <CollectionButton onClick={() => setSelect(!select)}>
                  <UncheckedIcon color={"#fff"} width={18} height={18} />
                </CollectionButton>
              ) : (
                <CollectionButton onClick={() => setSelect(!select)}>
                  <CheckedIcon color={"#fff"} width={18} height={18} />
                  <ButtonText>{selectedCollections?.length}/20</ButtonText>
                </CollectionButton>
              )} */}
              <CollectionButton onClick={selectAllCollections}>
                <ButtonText $click>Select All</ButtonText>
              </CollectionButton>
              <ButtonText
                $click
                onClick={() => {
                  setSelectedCollections([]);
                }}
              >
                Clear
              </ButtonText>
            </Row>
            <Row>
              <DeleteButton
                disabled={!selectedCollections?.length || !user}
                onClick={() => {
                  if (!selectedCollections?.length || !user) return;
                  setShowDeleteConfirm(true);
                }}
              >
                <LottieIcon
                  animationData={deleteAnimation}
                  trigger="hover"
                  size={24}
                />
              </DeleteButton>
              <DownloadButton
                $white
                onClick={handleDownloadSelected}
                style={{
                  pointerEvents:
                    !selectedCollections?.length || !user ? "none" : "all",
                }}
              >
                <LottieIcon
                  animationData={downloadAnimation}
                  trigger="click"
                  size={24}
                />
              </DownloadButton>
            </Row>
          </BigRowMobile>
        )}
        <ContentContainer>
          {!isFeedView && <WaterSeparator src={LineWater} alt="Line Water" />}
          {!loggedIn ? (
            <EmptyState>
              <h3>Login Required</h3>
              <p>You need to be logged in to view your videos.</p>
              <Button onClick={() => dispatch(setSignUpModalOpen(true))}>
                Login
              </Button>
            </EmptyState>
          ) : !data || (loading && !data.length) ? (
            isFeedView ? (
              <FeedSkeletonList>
                <FeedSkeleton>
                  <FeedSkeletonFrame />
                </FeedSkeleton>
              </FeedSkeletonList>
            ) : (
              <div style={{ paddingInline: "4px", width: "100%" }}>
                <VideoMasonrySkeletonLoader
                  count={24}
                  isCollectionPage
                  variant="collection"
                />
              </div>
            )
          ) : !data?.length ? (
            <Content>
              <EmptyState>
                {filter !== "ALL" ? (
                  <>
                    <h3>Your folder is empty</h3>
                    <p>No collections found</p>
                  </>
                ) : (
                  <>
                    <h3>
                      {search
                        ? "No search results"
                        : "Your collections are empty"}
                    </h3>
                    <p>
                      {search
                        ? `No videos found for "${search}"`
                        : "You haven't created any collections yet."}
                    </p>
                  </>
                )}

                {!search && filter === "ALL" && (
                  <Button onClick={() => navigate("/")}>Go Explore</Button>
                )}
              </EmptyState>
            </Content>
          ) : isFeedView ? (
            <Suspense fallback={null}>
              {(() => {
                const WINDOW_HALF = 3;
                const totalItems = data.length;
                const startIdx = Math.max(0, activeIndex - WINDOW_HALF);
                const endIdx = Math.min(
                  totalItems,
                  activeIndex + WINDOW_HALF + 1,
                );
                const before = startIdx * itemHeight;
                const after = Math.max(0, totalItems - endIdx) * itemHeight;
                return (
                  <FeedList ref={setFeedScrollEl}>
                    {before > 0 && (
                      <div
                        aria-hidden
                        style={{
                          height: `${before}px`,
                          flexShrink: 0,
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    {data.slice(startIdx, endIdx).map((item, i) => {
                      const realIdx = startIdx + i;
                      const distance = Math.abs(realIdx - activeIndex);
                      const priority = distance <= 1 ? "near" : "far";
                      return (
                        <ExploreFeedItem
                          key={item._id}
                          item={item}
                          priority={priority}
                          onItemClick={() => handleCollectionItemClick(item)}
                          onAddToFolder={(it) => {
                            setSelectedCollectionVideo(it);
                            setIsAddToFolderModalOpen(true);
                          }}
                          onDownload={(it) => {
                            const mediaUrl =
                              it?.output?.type === MEDIA_TYPE.IMAGE
                                ? it?.output?.image?.url
                                : it?.output?.video?.url;
                            if (!mediaUrl) {
                              toast.error("No media URL");
                              return;
                            }
                            window.location.href = mediaUrl;
                          }}
                          onDelete={(it) => {
                            setSelectedCollectionVideo(it);
                            setSelectedCollections([it._id]);
                            setShowDeleteConfirm(true);
                          }}
                        />
                      );
                    })}
                    {after > 0 && (
                      <div
                        aria-hidden
                        style={{
                          height: `${after}px`,
                          flexShrink: 0,
                          pointerEvents: "none",
                        }}
                      />
                    )}
                    <FeedSentinel ref={feedSentinelRef} aria-hidden />
                    {!maxReached && loading && data.length > 0 && (
                      <FeedLoader aria-live="polite" aria-label="Loading more">
                        <span />
                        <span />
                        <span />
                      </FeedLoader>
                    )}
                    {maxReached && data.length > 0 && (
                      <FeedEndMarker>You're all caught up</FeedEndMarker>
                    )}
                  </FeedList>
                );
              })()}
            </Suspense>
          ) : (
            <Content>
              {Array.from({ length: numOfColumns }).map((_, colIndex) => (
                <Column key={colIndex}>
                  {data
                    .filter((_, i) => i % numOfColumns === colIndex)
                    .map((item, rowIndex) => (
                      <CollectionItem
                        select={select}
                        key={item._id}
                        video={item}
                        onOpenVideoModal={() =>
                          setIsGenerateCollectionModalOpen(true)
                        }
                        onCollectionItemClick={() =>
                          handleCollectionItemClick(item)
                        }
                        priority={rowIndex < 4}
                        setSelectedCollections={setSelectedCollections}
                        selectedCollections={selectedCollections}
                      />
                    ))}
                </Column>
              ))}
            </Content>
          )}
        </ContentContainer>
        {!isFeedView && (
          <PaginationWrapper>
            <Pagination
              currentPage={page}
              totalCount={total}
              perPage={20}
              onPageChange={(page) => setPage(page)}
            />
          </PaginationWrapper>
        )}
        {!isFeedView && <AboutFapifyAccordion />}
      </Container>

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => setIsGenerateCollectionModalOpen(false)}
      />

      <CollectionModal
        isOpen={openCollectionModal}
        onClose={handleCollectionModalClose}
        video={selectedCollectionVideo}
        onOpenVideoModal={() => setIsGenerateCollectionModalOpen(true)}
        onDeleteClick={() => setShowDeleteConfirm(true)}
        onRateClick={handleRateCollection}
        onFolderClick={() => setIsAddToFolderModalOpen(true)}
        onSettingsClick={() => setIsSettingsModalOpen(true)}
        setSettingsPrompt={setSettingsPrompt}
        setShowExtendVideoModal={setShowExtendVideoModal}
        rating={selectedCollectionVideo?.rating ?? 0}
        onNavigate={handleNavigateCollection}
        hasPrev={hasPrev}
        hasNext={hasNext}
        navLoading={navLoading}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Video"
        message={
          selectedCollections?.length > 1 && select
            ? "Are you sure you want to delete these videos? This action cannot be undone."
            : "Are you sure you want to delete this video? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonType="danger"
        isLoading={deleteCollectionPending}
        theme="dark"
      />

      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => setIsCreateFolderModalOpen(false)}
        onConfirm={handleCreateFolder}
        folderName={folderName}
        setFolderName={setFolderName}
        isLoading={createFolderLoading || addCollectionToFolderLoading}
      />

      <AddToFolderModal
        isOpen={isAddToFolderModalOpen}
        onClose={() => setIsAddToFolderModalOpen(false)}
        onConfirm={handleAddToFolder}
        folders={folders}
        selectedCollectionFolders={selectedCollectionVideo?.folders ?? []}
        isLoading={addCollectionToFolderLoading}
        openCreateFolderModal={openCreateFolderModal}
        setIsCreatingNew={setIsCreatingNew}
        video={selectedCollectionVideo}
        isCreatingNew={isCreatingNew}
        closeCollectionModal={() => {
          setOpenCollectionModal(false);
        }}
      />

      <EditOrDeleteFolderModal
        isOpen={isEditOrDeleteFolderModalOpen}
        onClose={() => setIsEditOrDeleteFolderModalOpen(false)}
        onEdit={handleEditFolder}
        onConfirm={handleDeleteFolderModal}
        selectedFolder={selectedDeleteFolder}
        isEditLoading={editFolderLoading}
        isLoading={deleteFolderLoading}
      />

      <CollectionSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onCopy={handleCopyPrompt}
        settingsPrompt={settingsPrompt}
      />

      <ExtendVideoModal
        isOpen={showExtendVideoModal}
        onClose={() => setShowExtendVideoModal(false)}
        setIsEnterPromptModalOpen={setIsEnterPromptModalOpen}
        onConfirm={() => {
          onGenerateClick();
        }}
        setSelectedModel={setSelectedModelForExtend}
        setExtendVideoPrice={setExtendVideoPrice}
        selectedCollectionVideo={selectedCollectionVideo}
        totalPriceVideoExtend={calculateTotalPrice(extendVideoPrice)}
        setSelectedExtendCollection={setSelectedExtendCollection}
      />

      <EnterPromptModal
        isOpen={isEnterPromptModalOpen}
        onClose={() => setIsEnterPromptModalOpen(false)}
        setNegativePrompt={setNegativePrompt}
        customPrompt={customPrompt}
        negativePrompt={negativePrompt}
        setCustomPrompt={setCustomPrompt}
        videoResolution={videoResolution}
        setVideoResolution={setVideoResolution}
        enableVideoQuality={!isImageOutput(selectedCollectionVideo?.modelName)}
        modelPrice={extendVideoPrice}
        setCustomSeed={setCustomSeed}
        customSeed={customSeed}
        collectionSeed={selectedCollectionVideo?.seed}
        videoDuration={videoDuration}
        setVideoDuration={setVideoDuration}
        showPrompts={showPrompts}
        setShowPrompts={setShowPrompts}
        resetSeed={showExtendVideoModal}
        isMinVideoLength8sec={
          selectedExtendCollection?.output.videoDuration === 8
        }
        collectionId={selectedExtendCollection?._id}
      />
    </MainContainer>
  );
};

export default CollectionPage;
